import type { ConfidenceLevel, PotentialTier, ReadinessLevel, TrajectoryDirection } from '../../api/types';

/**
 * Presentation vocabulary for attributes.
 *
 * ---------------------------------------------------------------------
 * WHY BANDING HAPPENS HERE AND NOT IN THE DOMAIN
 *
 * This is the only place the UI turns an API value into something else, so
 * it is worth being precise about why that is not a domain calculation
 * leaking into the browser.
 *
 * docs/ASSESSMENT_FRAMEWORK.md §2.2 defines the five bands and then states
 * their status explicitly: "Bands are a **display aid** (colour/label
 * grouping in dense views), not a separate stored value — always derived
 * from the numeric rating." A band carries no information the rating does
 * not already carry; it is a way of drawing 14, not a second opinion about
 * it. Sending it over the wire would add a field that could disagree with
 * the number beside it.
 *
 * Everything that IS a judgement — the composite, its weighting, the
 * confidence level, the variance flag, the trajectory direction — arrives
 * from the API already decided, and this file never recomputes any of it.
 * ---------------------------------------------------------------------
 */

export interface Band {
  index: 1 | 2 | 3 | 4 | 5;
  label: string;
}

/** docs/ASSESSMENT_FRAMEWORK.md §2.2, transcribed exactly. */
export function bandFor(rating: number): Band {
  if (rating <= 4) return { index: 1, label: 'Significantly Below Expectation' };
  if (rating <= 8) return { index: 2, label: 'Developing' };
  if (rating <= 12) return { index: 3, label: 'Solid / Average' };
  if (rating <= 16) return { index: 4, label: 'Strong' };
  return { index: 5, label: 'Exceptional' };
}

/** Wording for the confidence levels, taken from
 * docs/ASSESSMENT_FRAMEWORK.md §4.1's own definitions. `unconfirmed` is
 * phrased as "not yet known" because §4.1 requires it read that way rather
 * than as a low opinion. */
export const CONFIDENCE_COPY: Record<ConfidenceLevel, { label: string; meaning: string }> = {
  unconfirmed: {
    label: 'Unconfirmed',
    meaning: 'Not yet established — no assessment, or a single unverified self-report.',
  },
  low: {
    label: 'Low confidence',
    meaning: 'A single source, or sources with no supporting evidence.',
  },
  medium: {
    label: 'Medium confidence',
    meaning: 'Several independent sources broadly agreeing, or one source with strong evidence.',
  },
  high: {
    label: 'High confidence',
    meaning: 'Several independent source types, broadly agreeing, with recent supporting evidence.',
  },
};

export const TRAJECTORY_COPY: Record<TrajectoryDirection, { glyph: string; label: string }> = {
  improving: { glyph: '▲', label: 'Improving' },
  stable: { glyph: '▬', label: 'Stable' },
  declining: { glyph: '▼', label: 'Declining' },
  insufficient_data: { glyph: '·', label: 'Insufficient history' },
};

/**
 * Constitution §12's ten named examples — a suggested picker, never a
 * closed enum (Mission 165 Part 4C, implemented Mission 166). `label`
 * stays free text at the API level; this list only pre-fills a
 * `<datalist>` so a caller who wants one of these does not have to type it
 * exactly, while anyone typing something else is never blocked.
 */
export const TALENT_DESIGNATION_SUGGESTIONS: readonly string[] = [
  'High Performer',
  'High Potential',
  'Emerging Talent',
  'Future Leader',
  'Specialist',
  'Versatile Talent',
  'Hidden Talent',
  'Critical Talent',
  'Succession Candidate',
  'Fast Developer',
];

/** `docs/DOMAIN_MODEL.md` §2.20's qualitative tier — deliberately not a
 * 1-20 number (Mission 004 stress test), so this is its own small
 * vocabulary rather than `bandFor`'s. */
export const POTENTIAL_TIER_OPTIONS: PotentialTier[] = ['limited', 'moderate', 'high', 'exceptional'];

export const POTENTIAL_TIER_LABEL: Record<PotentialTier, string> = {
  limited: 'Limited',
  moderate: 'Moderate',
  high: 'High',
  exceptional: 'Exceptional',
};

/** `docs/DOMAIN_MODEL.md` §2.21's qualitative status. */
export const READINESS_LEVEL_OPTIONS: ReadinessLevel[] = ['not_yet_ready', 'developing', 'ready_soon', 'ready'];

export const READINESS_LEVEL_LABEL: Record<ReadinessLevel, string> = {
  not_yet_ready: 'Not yet ready',
  developing: 'Developing',
  ready_soon: 'Ready soon',
  ready: 'Ready',
};

export const CONFIDENCE_OPTIONS: ConfidenceLevel[] = ['unconfirmed', 'low', 'medium', 'high'];

/** Source-type codes are the database's vocabulary; these are the words a
 * reader expects (docs/ASSESSMENT_FRAMEWORK.md §5.1's own presentation
 * example uses Self / Manager / Peers). An unrecognised code falls back to
 * itself rather than being hidden. */
export function sourceLabel(code: string): string {
  const labels: Record<string, string> = {
    self: 'Self',
    manager: 'Manager',
    peer: 'Peers',
    direct_report: 'Direct reports',
    objective_measurement: 'Objective measurement',
    structured_assessment: 'Structured assessment',
    system_derived: 'System derived',
  };
  return labels[code] ?? code;
}

/**
 * Evidence-type codes and their labels, transcribed from the seeded
 * `evidence_type` reference data (`database/migrations/0022_seed_reference_data.js`)
 * — the only place this vocabulary is defined. No endpoint lists it, so
 * this is a transcription of the same ten codes, not a second source of
 * truth for what the codes mean: the API alone decides which are
 * permitted for a given attribute, and refuses an unpermitted one
 * (`evidence_type_not_permitted`) regardless of what this list offers.
 */
export const EVIDENCE_TYPE_OPTIONS: { code: string; label: string }[] = [
  { code: 'observed_behaviour', label: 'Observed behaviour' },
  { code: 'project_outcome', label: 'Project/work outcome' },
  { code: 'performance_example', label: 'Performance example' },
  { code: 'completed_work_item', label: 'Completed work item' },
  { code: 'prior_assessment_result', label: 'Prior assessment result' },
  { code: 'qualification', label: 'Qualification' },
  { code: 'achievement', label: 'Achievement' },
  { code: 'feedback', label: 'Feedback' },
  { code: 'measured_result', label: 'Measured result' },
  { code: 'document_reference', label: 'Document/reference' },
];

/** An unrecognised code falls back to itself rather than being hidden —
 * the same convention `sourceLabel` follows. */
export function evidenceTypeLabel(code: string): string {
  return EVIDENCE_TYPE_OPTIONS.find((option) => option.code === code)?.label ?? code;
}

/** `2026-06-15T09:00:00.000Z` → `15 Jun 2026`. Timestamps only: DATE values
 * arrive from the API already formatted as calendar dates (Mission 008's
 * api/src/dto/dates.ts), and must not be re-parsed here — doing so would
 * reintroduce exactly the timezone shift that fix removed.
 *
 * An instant is genuinely a point in time, so it IS converted to the
 * reader's local calendar day — that is the whole difference between this
 * function and `formatCalendarDate` below. The month abbreviations come
 * from the same table as that function rather than from `toLocaleDateString`
 * (Mission 011): the platform's `en-GB` short month for September is
 * "Sept", so the two formatters otherwise printed the same month
 * differently in the same view — a profile could show "as at 3 Sept 2026"
 * directly above "Obtained 10 Jun 2022". One vocabulary, one table. */
export function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

/** `2020-01-15` → `15 Jan 2020`, without ever constructing a Date — see
 * above. Purely a re-arrangement of the string the API already produced. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatCalendarDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;
  const [, year, month, day] = match;
  const monthLabel = MONTHS[Number(month) - 1] ?? month;
  return `${Number(day)} ${monthLabel} ${year}`;
}
